// Optimistic "pending runs" store — the web counterpart to iOS's ActiveRunsStore.
//
// Why this exists: `POST /runs/` is synchronous on the backend (run_engine runs
// step 0 + all auto-steps inline, then commits at the very end), so for pure
// text/PDF generation the request blocks for the WHOLE generation (10–90 s) and
// the run row isn't visible to `listRuns` until it commits. If the start screen
// simply `await`ed that POST before navigating, the "Generate" button would just
// spin with zero feedback and only redirect once generation was already done.
//
// Instead, `beginAndPostRun` inserts an in-memory placeholder pill, fires the
// POST decoupled from any component (so navigating away never aborts it), and
// the caller navigates immediately. The Create gallery renders these placeholders
// in its "Generating now" section. On success the placeholder is dropped and the
// runs/library queries are invalidated so the committed run/set surfaces; on
// failure the pill flips to a dismissible error (auto-GC'd after a grace window).
//
// In-memory only (no localStorage): a placeholder represents a live in-session
// fetch. A page refresh aborts the fetch but the server keeps going and commits
// the run, which `listRuns` then reconciles — matching iOS's hydrate model.
//
// Pattern mirrors projects/reviewed-sections-store.ts (pubsub + useSyncExternalStore).
import { useSyncExternalStore } from "react";
import { queryClient } from "@/api/query-client";
import { startRun } from "@/api/runs";
import type { StartInputs } from "@/api/create-types";

export interface PendingRun {
  placeholderId: string;
  frameworkSlug: string;
  frameworkTitle: string;
  status: "posting" | "failed";
  /** Raw error (formatted with generationErrorMessage at render time). */
  error?: unknown;
  startedAt: number;
}

// How long a failed pill lingers before auto-GC (mirrors iOS terminalRetention).
const FAILED_RETENTION_MS = 30_000;

type Listener = () => void;
const listeners = new Set<Listener>();

let runs: PendingRun[] = [];

function emit(next: PendingRun[]): void {
  runs = next;
  for (const l of listeners) l();
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSnapshot(): PendingRun[] {
  return runs;
}

const EMPTY: PendingRun[] = [];

/** React hook: the live list of optimistic pending runs. */
export function usePendingRuns(): PendingRun[] {
  return useSyncExternalStore(subscribe, getSnapshot, () => EMPTY);
}

export function dismissRun(placeholderId: string): void {
  emit(runs.filter((r) => r.placeholderId !== placeholderId));
}

/**
 * Insert a "posting" placeholder and fire `POST /runs/` in the background.
 * Returns the placeholder id (callers usually ignore it). The caller should
 * navigate to the Create gallery immediately after calling this.
 */
export function beginAndPostRun(args: {
  frameworkSlug: string;
  frameworkTitle: string;
  startInputs: StartInputs;
}): string {
  const placeholderId = crypto.randomUUID();
  emit([
    {
      placeholderId,
      frameworkSlug: args.frameworkSlug,
      frameworkTitle: args.frameworkTitle,
      status: "posting",
      startedAt: Date.now(),
    },
    ...runs,
  ]);

  void startRun(args.frameworkSlug, args.startInputs)
    .then(() => {
      // Drop the placeholder; surface the now-committed run (awaiting_user/
      // complete) on the gallery and the produced set in the Library.
      emit(runs.filter((r) => r.placeholderId !== placeholderId));
      void queryClient.invalidateQueries({ queryKey: ["active-runs"] });
      void queryClient.invalidateQueries({ queryKey: ["question-sets"] });
    })
    .catch((error) => {
      emit(
        runs.map((r) =>
          r.placeholderId === placeholderId ? { ...r, status: "failed", error } : r,
        ),
      );
      // Best-effort GC so a dismissed-or-not failed pill doesn't linger forever.
      setTimeout(() => dismissRun(placeholderId), FAILED_RETENTION_MS);
    });

  return placeholderId;
}
